import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom';
import { portalAPI } from '../lib/api';
import { LayoutDashboard, FolderOpen, Receipt, FileText, LogOut, Shield, Calendar, ArrowLeft, Clock, Download } from 'lucide-react';
import { formatCurrency, formatDate, formatTime } from '../lib/utils';
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';
import KpiBar from '../components/KpiBar';
import EmptyState from '../components/EmptyState';

export default function PortalLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('mlx_portal_token');
    if (!token) { navigate('/portail/login'); return; }
    portalAPI.me().then(r => { setUser(r.data); setLoading(false); }).catch(() => {
      localStorage.removeItem('mlx_portal_token');
      localStorage.removeItem('mlx_portal_user');
      navigate('/portail/login');
    });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('mlx_portal_token');
    localStorage.removeItem('mlx_portal_user');
    navigate('/portail/login');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}><div style={{ color: 'var(--text-tertiary)' }}>Chargement...</div></div>;

  const navItems = [
    { path: '/portail', label: 'Tableau de bord', icon: LayoutDashboard, exact: true },
    { path: '/portail/dossiers', label: 'Mes dossiers', icon: FolderOpen },
    { path: '/portail/factures', label: 'Mes factures', icon: Receipt },
    { path: '/portail/contrats', label: 'Mes contrats', icon: FileText },
  ];

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg-primary)' }} data-testid="portal-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex w-[240px] flex-col fixed left-0 top-0 h-screen" style={{ background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-color)' }}>
        <div className="h-[64px] flex items-center px-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
          <img src="/branding/logo-white.png" alt="Adalia" className="h-10" />
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
            return (
              <Link key={item.path} to={item.path} className="flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-[13px] font-medium" style={isActive ? { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-soft)' } : { color: 'var(--text-muted)' }} data-testid={`portal-nav-${item.path.replace(/\//g, '-')}`}>
                <Icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          <div className="text-[12px] font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{user?.client?.nom_client || user?.nom}</div>
          <div className="text-[10px] mb-3" style={{ color: 'var(--text-tertiary)' }}>{user?.email}</div>
          <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-[8px] text-[12px]" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }} data-testid="portal-logout">
            <LogOut className="w-3.5 h-3.5" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col md:ml-[240px]">
        <header className="h-[64px] flex items-center justify-between px-6" style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-500" />
            <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>Espace client sécurisé</span>
          </div>
          <div className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{user?.client?.nom_client}</div>
        </header>
        <main className="flex-1 p-6 overflow-auto">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}

// Sub-pages below
export function PortalDashboard() {
  const { user } = useOutletContext() || {};
  const [data, setData] = useState(null);

  useEffect(() => {
    portalAPI.dashboard().then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <PageLoader />;

  const kpis = [
    { label: 'Dossiers actifs', value: data.stats?.dossiers_actifs ?? 0 },
    { label: 'Factures impayées', value: data.stats?.factures_impayees ?? 0 },
    { label: 'Montant dû', value: formatCurrency(data.stats?.montant_du, 'GNF'), isText: true },
    { label: 'Audiences à venir', value: data.stats?.audiences_a_venir ?? 0 },
  ];

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <PageHeader
        title={<>Bonjour, <span className="text-[#C9A227]">{user?.client?.nom_client || user?.nom}</span></>}
        subtitle="Voici un aperçu de vos dossiers et factures"
      />

      <KpiBar items={kpis} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <h2 className="text-[11px] uppercase tracking-[2px] mb-3" style={{ color: 'var(--text-tertiary)' }}>Dossiers récents</h2>
          <div className="space-y-2">
            {(data.dossiers_recents || []).length === 0 && <div className="text-[12px] py-4" style={{ color: 'var(--text-tertiary)' }}>Aucun dossier</div>}
            {(data.dossiers_recents || []).map(d => (
              <Link key={d.id} to={`/portail/dossiers/${d.id}`} className="block rounded-[10px] p-3 transition-colors hover:border-[#C9A227]" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>{d.numero_dossier}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: 'rgba(201,162,39,0.12)', color: '#C9A227' }}>{d.statut}</span>
                </div>
                <div className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>{d.intitule}</div>
              </Link>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-[11px] uppercase tracking-[2px] mb-3" style={{ color: 'var(--text-tertiary)' }}>Factures récentes</h2>
          <div className="space-y-2">
            {(data.factures_recentes || []).length === 0 && <div className="text-[12px] py-4" style={{ color: 'var(--text-tertiary)' }}>Aucune facture</div>}
            {(data.factures_recentes || []).map(f => (
              <div key={f.id} className="rounded-[10px] p-3" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>{f.numero_facture}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: f.statut === 'Payée' ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)', color: f.statut === 'Payée' ? '#22C55E' : '#F59E0B' }}>{f.statut}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{f.periode || formatDate(f.date_emission)}</div>
                  <div className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>{formatCurrency(f.montant_ttc, f.devise)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PortalDossiers() {
  const [dossiers, setDossiers] = useState([]);
  useEffect(() => { portalAPI.dossiers().then(r => setDossiers(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="portal-dossiers">
      <PageHeader title="Mes dossiers" subtitle="Suivez l'avancement de vos affaires" />
      {dossiers.length === 0 ? (
        <EmptyState icon={FolderOpen} title="Aucun dossier" subtitle="Aucun dossier ne vous est rattaché pour le moment." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {dossiers.map(d => (
            <Link key={d.id} to={`/portail/dossiers/${d.id}`} className="rounded-[12px] p-4 transition-colors hover:border-[#C9A227]" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>{d.numero_dossier}</span>
                <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: 'rgba(201,162,39,0.12)', color: '#C9A227' }}>{d.statut}</span>
              </div>
              <div className="text-[14px] font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{d.intitule}</div>
              <div className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{d.type_affaire} • {d.juridiction}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortalFactures() {
  const [factures, setFactures] = useState([]);
  useEffect(() => { portalAPI.factures().then(r => setFactures(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="portal-factures">
      <PageHeader title="Mes factures" subtitle="Consultez et téléchargez vos factures" />
      {factures.length === 0 ? (
        <EmptyState icon={Receipt} title="Aucune facture" subtitle="Vous n'avez aucune facture pour le moment." />
      ) : (
        <div className="rounded-[12px] overflow-hidden" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
          <div className="grid grid-cols-12 gap-4 px-4 py-3 text-[10px] uppercase tracking-[1px]" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
            <div className="col-span-3">Numéro</div>
            <div className="col-span-3">Période</div>
            <div className="col-span-2">Émission</div>
            <div className="col-span-2 text-right">Montant TTC</div>
            <div className="col-span-2">Statut</div>
          </div>
          {factures.map(f => (
            <Link key={f.id} to={`/portail/factures/${f.id}`} className="grid grid-cols-12 gap-4 px-4 py-3 items-center transition-colors hover:bg-[var(--bg-tertiary)]" style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
              <div className="col-span-3 text-[12px] font-medium" style={{ color: '#C9A227' }}>{f.numero_facture}</div>
              <div className="col-span-3 text-[12px]" style={{ color: 'var(--text-secondary)' }}>{f.periode || '-'}</div>
              <div className="col-span-2 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{formatDate(f.date_emission)}</div>
              <div className="col-span-2 text-[12px] text-right" style={{ color: 'var(--text-primary)' }}>{formatCurrency(f.montant_ttc, f.devise)}</div>
              <div className="col-span-2"><span className="text-[10px] px-2 py-0.5 rounded" style={{ background: f.statut === 'Payée' ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)', color: f.statut === 'Payée' ? '#22C55E' : '#F59E0B' }}>{f.statut}</span></div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PortalContrats() {
  const [contrats, setContrats] = useState([]);
  useEffect(() => { portalAPI.contrats().then(r => setContrats(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="portal-contrats">
      <PageHeader title="Mes contrats" subtitle="Vos contrats et conventions en cours" />
      {contrats.length === 0 ? (
        <EmptyState icon={FileText} title="Aucun contrat" subtitle="Aucun contrat n'est associé à votre compte." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {contrats.map(c => (
            <div key={c.id} className="rounded-[12px] p-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }} data-testid="portal-contrat-card">
              <div className="text-[10px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{c.reference || c.numero_contrat}</div>
              <div className="text-[14px] font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{c.description || c.type_contrat}</div>
              <div className="flex items-center justify-between text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
                <span>{c.type_contrat}</span>
                {c.montant_ht ? <span style={{ color: 'var(--text-secondary)' }}>{formatCurrency(c.montant_ht, c.devise)}{c.frequence ? ` / ${c.frequence.toLowerCase()}` : ''}</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Helpers détail ───────────────────────────────────────────────
function PortalStatutChip({ statut }) {
  const paid = statut === 'Payée';
  const late = statut === 'En retard';
  const bg = paid ? 'rgba(34,197,94,0.12)' : late ? 'rgba(220,38,38,0.12)' : 'rgba(245,158,11,0.12)';
  const color = paid ? '#22C55E' : late ? '#DC2626' : '#F59E0B';
  return <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: bg, color }}>{statut}</span>;
}

export function PortalDossierDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [telechargement, setTelechargement] = useState('');

  useEffect(() => {
    portalAPI.dossier(id).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
    portalAPI.documents(id).then(r => setPieces(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [id]);

  const telecharger = async (p) => {
    setTelechargement(p.id);
    try { await portalAPI.downloadDocument(p.id, p.filename); } catch { /* rien : le bouton se réactive */ }
    setTelechargement('');
  };

  const back = (
    <Link to="/portail/dossiers" className="inline-flex items-center gap-2 text-[12px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
      <ArrowLeft className="w-3.5 h-3.5" /> Mes dossiers
    </Link>
  );

  if (loading) return <PageLoader />;
  if (!data?.dossier) return <div>{back}<EmptyState icon={FolderOpen} title="Dossier introuvable" subtitle="Ce dossier n'existe pas ou ne vous est pas accessible." /></div>;

  const d = data.dossier;
  const audiences = data.audiences || [];
  const echeances = data.echeances || [];

  return (
    <div className="space-y-6" data-testid="portal-dossier-detail">
      <div>
        {back}
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-serif" style={{ fontSize: 26, fontWeight: 400, letterSpacing: '-0.012em', lineHeight: 1.08, color: 'var(--text-primary)' }}>{d.intitule}</h1>
          <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: 'rgba(201,162,39,0.12)', color: '#C9A227' }}>{d.statut}</span>
        </div>
        <div className="text-[12px] mt-1" style={{ color: 'var(--text-tertiary)' }}>{d.numero_dossier} • {d.type_affaire}{d.juridiction ? ` • ${d.juridiction}` : ''}</div>
      </div>

      <section>
        <h2 className="text-[11px] uppercase tracking-[2px] mb-3 flex items-center gap-2" style={{ color: 'var(--text-tertiary)' }}><Calendar className="w-3.5 h-3.5" /> Audiences</h2>
        {audiences.length === 0 ? (
          <div className="text-[12px] py-3" style={{ color: 'var(--text-tertiary)' }}>Aucune audience programmée.</div>
        ) : (
          <div className="space-y-2">
            {audiences.map(a => (
              <div key={a.id} className="flex items-center gap-3 rounded-[10px] p-3" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                <Calendar className="w-4 h-4 text-[#C9A227]" />
                <div className="flex-1">
                  <div className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>{a.objet || a.type_audience}</div>
                  <div className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{formatDate(a.date_audience)}{a.date_audience ? ` à ${formatTime(a.date_audience)}` : ''}</div>
                </div>
                <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{a.salle || a.juridiction || ''}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-[11px] uppercase tracking-[2px] mb-3 flex items-center gap-2" style={{ color: 'var(--text-tertiary)' }}><FileText className="w-3.5 h-3.5" /> Pièces du dossier</h2>
        {pieces.length === 0 ? (
          <div className="text-[12px] py-3" style={{ color: 'var(--text-tertiary)' }}>
            Aucune pièce partagée pour l'instant — votre avocat met à disposition les documents au fil du dossier.
          </div>
        ) : (
          <div className="space-y-2" data-testid="portal-pieces">
            {pieces.map(p => (
              <div key={p.id} className="flex items-center gap-3 rounded-[10px] p-3" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                <FileText className="w-4 h-4 text-[#C9A227]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>{p.filename}</div>
                  <div className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
                    {p.size ? `${(p.size / 1024 / 1024).toFixed(1)} Mo` : ''}{p.partage_le ? ` • partagée le ${formatDate(p.partage_le)}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => telecharger(p)}
                  disabled={telechargement === p.id}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] disabled:opacity-50"
                  style={{ background: 'rgba(201,162,39,0.12)', color: '#C9A227', border: '1px solid rgba(201,162,39,0.22)' }}
                >
                  <Download className="w-3.5 h-3.5" /> Télécharger
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-[11px] uppercase tracking-[2px] mb-3 flex items-center gap-2" style={{ color: 'var(--text-tertiary)' }}><Clock className="w-3.5 h-3.5" /> Échéances</h2>
        {echeances.length === 0 ? (
          <div className="text-[12px] py-3" style={{ color: 'var(--text-tertiary)' }}>Aucune échéance.</div>
        ) : (
          <div className="space-y-2">
            {echeances.map(e => (
              <div key={e.id} className="flex items-center gap-3 rounded-[10px] p-3" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                <Clock className="w-4 h-4 text-[#C9A227]" />
                <div className="flex-1">
                  <div className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>{e.description}</div>
                  <div className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{e.type_echeance}{e.date_echeance ? ` • ${formatDate(e.date_echeance)}` : ''}</div>
                </div>
                {e.statut ? <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>{e.statut}</span> : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function PortalFactureDetail() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalAPI.facture(id).then(r => { setF(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  const back = (
    <Link to="/portail/factures" className="inline-flex items-center gap-2 text-[12px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
      <ArrowLeft className="w-3.5 h-3.5" /> Mes factures
    </Link>
  );

  if (loading) return <PageLoader />;
  if (!f?.id) return <div>{back}<EmptyState icon={Receipt} title="Facture introuvable" subtitle="Cette facture n'existe pas ou ne vous est pas accessible." /></div>;

  const lignes = f.lignes || [];

  return (
    <div className="space-y-6 max-w-2xl" data-testid="portal-facture-detail">
      <div>
        {back}
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-serif" style={{ fontSize: 26, fontWeight: 400, letterSpacing: '-0.012em', lineHeight: 1.08, color: 'var(--text-primary)' }}>{f.numero_facture}</h1>
          <PortalStatutChip statut={f.statut} />
        </div>
        <div className="text-[12px] mt-1" style={{ color: 'var(--text-tertiary)' }}>{f.periode || ''}{f.periode ? ' • ' : ''}Émise le {formatDate(f.date_emission)}{f.date_echeance ? ` • Échéance ${formatDate(f.date_echeance)}` : ''}</div>
      </div>

      <div className="rounded-[12px] overflow-hidden" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
        {lignes.length > 0 && (
          <>
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-[10px] uppercase tracking-[1px]" style={{ color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
              <div className="col-span-6">Description</div>
              <div className="col-span-2 text-right">Qté</div>
              <div className="col-span-2 text-right">P.U.</div>
              <div className="col-span-2 text-right">Total</div>
            </div>
            {lignes.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 px-4 py-3 text-[12px] items-center" style={{ borderBottom: '1px solid var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                <div className="col-span-6" style={{ color: 'var(--text-primary)' }}>{l.description}</div>
                <div className="col-span-2 text-right">{l.quantite}</div>
                <div className="col-span-2 text-right">{formatCurrency(l.prix_unitaire, f.devise)}</div>
                <div className="col-span-2 text-right" style={{ color: 'var(--text-primary)' }}>{formatCurrency(l.total, f.devise)}</div>
              </div>
            ))}
          </>
        )}
        <div className="px-4 py-3 space-y-1.5">
          <div className="flex items-center justify-between text-[12px]" style={{ color: 'var(--text-tertiary)' }}><span>Montant HT</span><span style={{ color: 'var(--text-secondary)' }}>{formatCurrency(f.montant_ht, f.devise)}</span></div>
          <div className="flex items-center justify-between text-[12px]" style={{ color: 'var(--text-tertiary)' }}><span>TVA{f.taux_tva != null ? ` (${f.taux_tva}%)` : ''}</span><span style={{ color: 'var(--text-secondary)' }}>{formatCurrency(f.montant_tva, f.devise)}</span></div>
          <div className="flex items-center justify-between pt-2 mt-1 border-t" style={{ borderColor: 'var(--border-color)' }}>
            <span className="text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>Montant TTC</span>
            <span className="text-[15px] font-serif tabular-nums" style={{ color: 'var(--text-primary)' }}>{formatCurrency(f.montant_ttc, f.devise)}</span>
          </div>
        </div>
      </div>

      {f.statut === 'Payée' && f.date_paiement && (
        <div className="text-[12px]" style={{ color: '#22C55E' }}>Réglée le {formatDate(f.date_paiement)}{f.mode_paiement ? ` — ${f.mode_paiement}` : ''}</div>
      )}
      {f.notes ? <div className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{f.notes}</div> : null}
    </div>
  );
}
